// Projects + project sharing + per-project memory client.
//
// Lifted out of `../api` so the file boundary matches the backend
// `src/railyard/routes/projects.py`. Functions here drive
// `/api/projects/*`. Activity / artifacts / cross-project deliverables
// stay in `../api` for now — those routes are tangential to the
// projects resource and live alongside other surfaces in the backend.

import {
  API,
  ApiError,
  encodePath,
  requestJson,
  requestOk,
  type MemoryDoc,
  type MemoryPreview,
} from "../api";

import type { components } from "@/lib/api-types";


/** Convenience alias — every server schema lives under components["schemas"]. */
type Schemas = components["schemas"];


export type ProjectSummary = Schemas["ProjectSummary"];


// ---------------------------------------------------------------------------
// Projects (multi memory_store)
// ---------------------------------------------------------------------------

export async function listProjects(
  opts?: { archived?: boolean; signal?: AbortSignal },
): Promise<ProjectSummary[]> {
  const qs = opts?.archived ? "?archived=true" : "";
  return requestJson(`${API}/api/projects${qs}`, { signal: opts?.signal });
}

export async function createProject(name: string, description?: string): Promise<ProjectSummary> {
  return requestJson(`${API}/api/projects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, description }),
  });
}

// The PATCH body and its echo both have generated schemas; use those rather
// than a hand-rolled subset, which drifts the moment the server grows a field
// (it had already missed `brand`, present on the wire since ENG-2303).
export type ProjectUpdate = Schemas["UpdateProjectRequest"];
export type ProjectUpdateResponse = Schemas["ProjectUpdateResponse"];

export async function updateProject(
  projectId: string,
  updates: ProjectUpdate,
): Promise<ProjectUpdateResponse> {
  return requestJson(`${API}/api/projects/${encodeURIComponent(projectId)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(updates),
  });
}

export type ProjectBrandFetchLogoResponse = Schemas["ProjectBrandFetchLogoResponse"];

/**
 * Fill a project's `brand.logo_file_id` from its saved `brand.domain`
 * (ENG-5089). Owner-only, and entirely server-side: the bytes are fetched
 * through our own egress, never the browser's, so a deal's domain is never
 * handed to a third-party host.
 *
 * Reads the STORED website, not whatever is typed in the dialog — save the
 * field first. Throws `ApiError`; see `isLogoFetchUnavailable` for telling an
 * API that predates this route from a domain with no usable mark.
 */
export async function fetchProjectBrandLogo(
  projectId: string,
): Promise<ProjectBrandFetchLogoResponse> {
  return requestJson(
    `${API}/api/projects/${encodeURIComponent(projectId)}/brand/fetch-logo`,
    { method: "POST" },
  );
}

/**
 * Whether a failed `fetchProjectBrandLogo` means the endpoint isn't deployed
 * yet, rather than that the lookup came back empty.
 *
 * Web ships on every push to `main` while the API waits for the daily deploy,
 * so for up to a day this button is talking to an API without the route — and
 * an unmatched FastAPI path answers 404, the same status the route itself uses
 * for "nothing renderable found". Every 404 the route raises carries a
 * structured `detail.code`, and the PRESENCE of one is the discriminator: a
 * coded 404 means the route ran and refused, an uncoded one means nothing
 * answered. Matching on the message text instead would be matching our own
 * prose across exactly the version skew this exists for, and reading one
 * specific code would misreport every other refusal the route grows later.
 */
export function isLogoFetchUnavailable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  if (err.status === 405) return true;
  if (err.status !== 404) return false;
  const detail = (err.body as { detail?: { code?: unknown } } | null)?.detail;
  return typeof detail?.code !== "string";
}



// ---------------------------------------------------------------------------
// Project sharing
// ---------------------------------------------------------------------------

// Pydantic Literal aliases don't get standalone schemas — they're inlined
// as enum members on each field. Mirror them here so other modules can
// import the named type.
export type ProjectPermission = Schemas["ProjectACLEntry"]["permission"];
export type ProjectVisibility = Schemas["ProjectSharing"]["visibility"];
export type ProjectACLEntry = Schemas["ProjectACLEntry"];
export type ProjectSharing = Schemas["ProjectSharing"];

export async function getProjectSharing(projectId: string): Promise<ProjectSharing> {
  return requestJson(`${API}/api/projects/${encodeURIComponent(projectId)}/sharing`);
}

export async function setProjectSharing(
  projectId: string,
  payload: { visibility?: ProjectVisibility; acl?: ProjectACLEntry[] },
): Promise<ProjectSharing> {
  return requestJson(`${API}/api/projects/${encodeURIComponent(projectId)}/sharing`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function deleteProject(projectId: string): Promise<void> {
  await requestOk(`${API}/api/projects/${encodeURIComponent(projectId)}`, {
    method: "DELETE",
  });
}


// ---------------------------------------------------------------------------
// Per-project memory
// ---------------------------------------------------------------------------

export async function listProjectMemories(projectId: string): Promise<MemoryPreview[]> {
  return requestJson(`${API}/api/projects/${encodeURIComponent(projectId)}/memories`);
}

export async function readProjectMemory(
  projectId: string,
  topic: string,
  signal?: AbortSignal,
): Promise<MemoryDoc> {
  return requestJson(
    `${API}/api/projects/${encodeURIComponent(projectId)}/memories/${encodePath(topic)}`,
    { signal },
  );
}

/** Read a project memory topic; 404 → null ("topic not set yet"). */
export async function readProjectMemoryOptional(
  projectId: string,
  topic: string,
  signal?: AbortSignal,
): Promise<MemoryDoc | null> {
  const r = await requestOk(
    `${API}/api/projects/${encodeURIComponent(projectId)}/memories/${encodePath(topic)}`,
    { signal },
    { allowStatus: [404] },
  );
  if (r.status === 404) return null;
  return r.json() as Promise<MemoryDoc>;
}

export async function writeProjectMemory(projectId: string, topic: string, body: string): Promise<void> {
  await requestOk(
    `${API}/api/projects/${encodeURIComponent(projectId)}/memories/${encodePath(topic)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    },
  );
}

export async function deleteProjectMemory(projectId: string, topic: string): Promise<void> {
  await requestOk(
    `${API}/api/projects/${encodeURIComponent(projectId)}/memories/${encodePath(topic)}`,
    { method: "DELETE" },
  );
}

export async function renameProjectMemory(
  projectId: string,
  topic: string,
  newTopic: string,
): Promise<{ topic: string; path: string }> {
  return requestJson(
    `${API}/api/projects/${encodeURIComponent(projectId)}/memories/${encodePath(topic)}/rename`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ new_topic: newTopic }),
    },
  );
}
